import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { fetchSavedQueries, saveSavedQuery, deleteSavedQuery } from '../services/api';

// FastAPI server URL for WebSocket and API endpoints
// Use environment variable, or window.location.origin for production, or fallback to localhost for dev
const FASTAPI_URL = process.env.REACT_APP_FASTAPI_URL || 
  (typeof window !== 'undefined' ? window.location.origin : 'http://127.0.0.1:7860');

// Construct WebSocket URL from HTTP URL
// Convert http://host:port to ws://host:port or https://host:port to wss://host:port
const getWebSocketURL = (httpUrl) => {
  if (typeof window === 'undefined') {
    return httpUrl.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  }
  // Use current window location for WebSocket
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}`;
};

const FASTAPI_WS_URL = getWebSocketURL(FASTAPI_URL);

const ChatInterface = ({ dbConnection, onNewChat, onBack, agent, setAgent, session }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'agent',
      content: "Hello! I'm FlashAI. How can I help you with your database today?",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('');
  const [executionTimer, setExecutionTimer] = useState(0);
  const [isExecuting, setIsExecuting] = useState(false);
  const [savedQueries, setSavedQueries] = useState([]);
  const [savedOpen, setSavedOpen] = useState(false);
  const [savedLoading, setSavedLoading] = useState(false);
  const executionTimerRef = useRef(null);
  const executionTimerValueRef = useRef(0); // Store current timer value for reliable capture
  const timerStartedRef = useRef(false); // Track if timer has been started for current query
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const textareaRef = useRef(null);

  const getUserDisplayName = (s) => {
    return (
      s?.display_name ||
      s?.profile?.display_name ||
      s?.username ||
      s?.profile?.username ||
      s?.email ||
      s?.profile?.email ||
      'User'
    );
  };

  const formatElapsed = (seconds) => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
    const mm = String(Math.floor(safeSeconds / 60)).padStart(2, '0');
    const ss = String(safeSeconds % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  };
  // State for table sorting and grouping per message
  const [tableState, setTableState] = useState({}); // { messageId: { sortColumn, sortDirection, groupByColumn, groupByGranularity, groupByIsDateColumn, isMonthLevelDate, searchFilters: [{id, column, term, operator}] } }
  const [openDropdowns, setOpenDropdowns] = useState({}); // { messageId: boolean } - tracks which group by dropdown is open
  const [openSearchDropdowns, setOpenSearchDropdowns] = useState({}); // { messageId: boolean } - tracks which search dropdown is open
  const [openOperatorDropdowns, setOpenOperatorDropdowns] = useState({}); // { messageId: { filterId: boolean } } - tracks which operator dropdown is open for each filter
  const [openExportDropdowns, setOpenExportDropdowns] = useState({}); // { messageId: boolean } - tracks which export dropdown is open
  const [activeFilterInput, setActiveFilterInput] = useState({}); // { messageId: filterId } - tracks which filter is being edited
  const [showGuideModal, setShowGuideModal] = useState(false); // State for guide PDF modal
  const [showPromptsModal, setShowPromptsModal] = useState(false); // State for prompts Excel modal
  const [excelData, setExcelData] = useState(null); // State for Excel data

  // Function to convert backend status messages to user-friendly messages
  const translateStatusMessage = (statusMessage) => {
    // Remove emojis using comprehensive regex (includes all emoji ranges)
    const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FAFF}]/gu;
    const textWithoutEmoji = statusMessage.replace(emojiRegex, '').trim();
    
    // Map technical messages to generic, non-technical, interesting status messages
    const statusMapping = {
      'Analyzing your request...': 'FlashAI is working on it',
      'Understanding your query...': 'FlashAI is thinking',
      'Gathering more information...': 'FlashAI needs more details',
      'Mapping to database schema...': 'FlashAI is getting ready',
      'Loading database metadata...': 'FlashAI is looking things up',
      'Generating SQL query...': 'FlashAI is crafting a response',
      'Executing query...': 'FlashAI is almost there',
      'Preparing for next query...': 'Preparing for next question',
      'Processing...': 'FlashAI is thinking'
    };
    
    // Try to find a mapping, otherwise return a friendly version
    for (const [key, value] of Object.entries(statusMapping)) {
      if (textWithoutEmoji.includes(key)) {
        return value;
      }
    }
    
    // If no match, return a generic friendly message based on keywords (non-technical)
    if (textWithoutEmoji.toLowerCase().includes('analyzing') || textWithoutEmoji.toLowerCase().includes('route')) {
      return 'FlashAI is working on it';
    } else if (textWithoutEmoji.toLowerCase().includes('understanding') || textWithoutEmoji.toLowerCase().includes('classify')) {
      return 'FlashAI is thinking';
    } else if (textWithoutEmoji.toLowerCase().includes('generating')) {
      return 'FlashAI is crafting a response';
    } else if (textWithoutEmoji.toLowerCase().includes('executing') || textWithoutEmoji.toLowerCase().includes('execute')) {
      return 'FlashAI is almost there';
    } else if (textWithoutEmoji.toLowerCase().includes('thinking')) {
      return 'FlashAI is thinking';
    } else if (textWithoutEmoji.toLowerCase().includes('preparing')) {
      return 'Preparing for next question';
    } else if (textWithoutEmoji.toLowerCase().includes('loading') || textWithoutEmoji.toLowerCase().includes('metadata')) {
      return 'FlashAI is looking things up';
    } else if (textWithoutEmoji.toLowerCase().includes('mapping') || textWithoutEmoji.toLowerCase().includes('schema')) {
      return 'FlashAI is getting ready';
    }
    
    return 'FlashAI is working on it';
  };

  const savedQuerySet = useMemo(() => {
    return new Set(savedQueries.map((item) => item.query));
  }, [savedQueries]);

  const loadSavedQueries = useCallback(async () => {
    if (!session?.access) return;
    setSavedLoading(true);
    try {
      const data = await fetchSavedQueries(session.access);
      setSavedQueries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('Failed to load saved queries:', err);
    } finally {
      setSavedLoading(false);
    }
  }, [session?.access]);

  useEffect(() => {
    loadSavedQueries();
  }, [loadSavedQueries]);

  const handleToggleSaveQuery = async (queryText) => {
    if (!session?.access || !queryText?.trim()) return;
    const existing = savedQueries.find((item) => item.query === queryText);
    try {
      if (existing) {
        await deleteSavedQuery(session.access, existing.id);
      } else {
        await saveSavedQuery(session.access, queryText);
      }
      await loadSavedQueries();
    } catch (err) {
      const message =
        err?.payload?.detail ||
        err?.message ||
        'Failed to update saved query.';
      if (typeof window !== 'undefined') {
        window.alert(message);
      }
      console.warn('Failed to update saved query:', err);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Get rows as currently displayed (filter + group + sort) for a given message
  const getProcessedRowsForExport = (result, messageId) => {
    const state = tableState[messageId] || {
      sortColumn: null,
      sortDirection: null,
      groupByColumn: null,
      groupByGranularity: null,
      groupByIsDateColumn: false,
      isMonthLevelDate: false,
      searchFilters: []
    };

    const groupByColumnIndex = state.groupByColumn;
    const isDateGroupingColumn = state.groupByIsDateColumn;

    // Re-check if it's a month-level date (same logic as render)
    const detectedIsMonthLevel = groupByColumnIndex !== null
      ? isMonthLevelDate(result.rows, groupByColumnIndex)
      : false;
    const isMonthLevelDateColumn = state.isMonthLevelDate || detectedIsMonthLevel;

    const effectiveGranularity = isDateGroupingColumn
      ? (state.groupByGranularity || (isMonthLevelDateColumn ? 'month' : 'day'))
      : null;

    // Start from all rows
    let processedRows = result.rows;

    // Apply multiple search filters first (before grouping)
    const searchFilters = state.searchFilters || [];
    if (searchFilters.length > 0) {
      processedRows = filterRowsMultiple(processedRows, searchFilters);
    }

    // Apply grouping (aggregate to one row per group)
    if (groupByColumnIndex !== null) {
      const groupedData = groupRows(processedRows, groupByColumnIndex, {
        isDateColumn: isDateGroupingColumn,
        dateGranularity: effectiveGranularity || 'day'
      });
      processedRows = groupedData.map(group => group.aggregatedRow);
    }

    // Apply sorting
    if (state.sortColumn !== null && state.sortDirection) {
      processedRows = sortRows(processedRows, state.sortColumn, state.sortDirection);
    }

    return processedRows;
  };

  const exportToExcel = (result, messageId, showAll = false, isFiltered = false) => {
    try {
      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      // Use currently displayed rows (filtered / grouped / sorted)
      const processedRows = getProcessedRowsForExport(result, messageId);

      // Use all processed rows if showAll is true, otherwise use limited rows
      const rowsToExport = showAll ? processedRows : processedRows.slice(0, 12);
      
      // Convert the result data to worksheet format
      const wsData = [
        result.columns, // Header row
        ...rowsToExport   // Data rows
      ];
      
      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Data");
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      let filename;
      if (isFiltered) {
        const state = tableState[messageId] || {};
        const filterCount = (state.searchFilters || []).filter(f => f.term && f.term.trim() !== '').length;
        const filterLabel = filterCount > 0 ? `_${filterCount}filters` : '';
        filename = `flash_data_filtered${filterLabel}_${processedRows.length}rows_${timestamp}.xlsx`;
      } else if (showAll) {
        filename = `flash_data_all_${timestamp}.xlsx`;
      } else {
        filename = `flash_data_${timestamp}.xlsx`;
      }
      
      // Generate Excel file in memory and trigger browser download (no server-side file saving)
      const excelBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Clean up the object URL
      
      console.log(`Excel file exported: ${filename}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting to Excel. Please try again.');
    }
  };

  const exportToPowerBI = (result, messageId) => {
    try {
      // Convert data to CSV format
      const csvRows = [];
      
      // Add header row
      const headerRow = result.columns.map(col => {
        // Escape commas and quotes in column names
        const escaped = String(col).replace(/"/g, '""');
        return `"${escaped}"`;
      }).join(',');
      csvRows.push(headerRow);
      
      // Use currently displayed rows (filtered / grouped / sorted)
      const processedRows = getProcessedRowsForExport(result, messageId);

      // Add data rows
      processedRows.forEach(row => {
        const csvRow = row.map(cell => {
          // Handle null/undefined values
          if (cell === null || cell === undefined) {
            return '""';
          }
          // Convert to string and escape commas and quotes
          const cellStr = String(cell);
          const escaped = cellStr.replace(/"/g, '""');
          // Wrap in quotes to handle commas in the data
          return `"${escaped}"`;
        }).join(',');
        csvRows.push(csvRow);
      });
      
      // Combine all rows with newlines
      const csvContent = csvRows.join('\n');
      
      // Create a blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `powerbi_export_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Clean up the object URL
      
      console.log(`CSV file exported: ${filename}`);
    } catch (error) {
      console.error('Error exporting to PowerBI:', error);
      alert('Error exporting to PowerBI. Please try again.');
    }
  };

  // Helper function to detect if value is numeric
  const isNumeric = (value) => {
    if (value === null || value === undefined || value === '') return false;
    const num = Number(value);
    return !isNaN(num) && isFinite(num);
  };

  const DATE_GRANULARITY_OPTIONS = [
    { value: 'day', label: 'Day' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' }
  ];

  const DATE_STRING_PATTERNS = [
    /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?)?$/, // 2024-01-31 or ISO
    /^\d{4}\/\d{2}\/\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/, // 2024/01/31
    /^\d{2}\/\d{2}\/\d{4}(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/, // 01/31/2024
    /^\d{2}-\d{2}-\d{4}(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/, // 31-01-2024 or 01-31-2024
    /^\d{8}$/, // 20240131
    /^\d{4}$/, // 2024
    /^\d{4}-\d{1,2}$/, // 2024-01
    /^\d{1,2}-\d{4}$/ // 01-2024 or 1-2024
  ];

  const parseDateValue = (value) => {
    if (value === null || value === undefined || value === '') return null;

    if (value instanceof Date && !isNaN(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;

      const matchesKnownPattern = DATE_STRING_PATTERNS.some(pattern => pattern.test(trimmed));
      if (!matchesKnownPattern) {
        return null;
      }

      // Normalize common separators
      const normalized = trimmed
        .replace(/\//g, '-')
        .replace('T', ' ');

      let isoCandidate = normalized;

      if (/^\d{4}$/.test(normalized)) {
        isoCandidate = `${normalized}-01-01`;
      } else if (/^\d{4}-\d{1,2}$/.test(normalized)) {
        const [yearPart, monthPartRaw] = normalized.split('-');
        const monthPart = String(monthPartRaw).padStart(2, '0');
        isoCandidate = `${yearPart}-${monthPart}-01`;
      } else if (/^\d{1,2}-\d{4}$/.test(normalized)) {
        const [monthPartRaw, yearPart] = normalized.split('-');
        const monthPart = String(monthPartRaw).padStart(2, '0');
        isoCandidate = `${yearPart}-${monthPart}-01`;
      }

      const date = new Date(isoCandidate);
      if (!isNaN(date)) {
        return date;
      }
    }

    return null;
  };

  const isDateColumnLike = (rows, columnIndex) => {
    if (!Array.isArray(rows) || rows.length === 0) return false;

    let samplesChecked = 0;
    let validDates = 0;

    for (const row of rows) {
      if (!Array.isArray(row)) continue;

      const value = row[columnIndex];
      if (value === null || value === undefined || value === '') {
        continue;
      }

      samplesChecked += 1;

      if (parseDateValue(value)) {
        validDates += 1;
      } else {
        return false;
      }

      if (samplesChecked >= 8) break;
    }

    return samplesChecked > 0 && validDates === samplesChecked;
  };

  // Check if a date column is already at month level (MM-YYYY format)
  const isMonthLevelDate = (rows, columnIndex) => {
    if (!Array.isArray(rows) || rows.length === 0) return false;

    // Pattern to match MM-YYYY format (e.g., "01-2025", "12-2024")
    const monthYearPattern = /^\d{1,2}-\d{4}$/;
    
    let samplesChecked = 0;
    let monthYearMatches = 0;

    for (const row of rows) {
      if (!Array.isArray(row)) continue;

      const value = row[columnIndex];
      if (value === null || value === undefined || value === '') {
        continue;
      }

      const strValue = String(value).trim();
      samplesChecked += 1;

      // Check if it matches MM-YYYY format
      if (monthYearPattern.test(strValue)) {
        const [monthPart, yearPart] = strValue.split('-');
        const month = parseInt(monthPart, 10);
        // Validate month is between 1-12
        if (month >= 1 && month <= 12) {
          monthYearMatches += 1;
        } else {
          return false;
        }
      } else {
        return false;
      }

      if (samplesChecked >= 8) break;
    }

    return samplesChecked > 0 && monthYearMatches === samplesChecked;
  };

  const formatDateByGranularity = (value, granularity = 'day') => {
    const date = parseDateValue(value);
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    if (granularity === 'year') {
      return `${year}`;
    }

    if (granularity === 'month') {
      return `${year}-${month}`;
    }

    return `${year}-${month}-${day}`;
  };

  // Sort rows by column index
  const sortRows = (rows, columnIndex, direction) => {
    if (!direction || columnIndex === null || columnIndex === undefined) return rows;
    
    const sorted = [...rows].sort((a, b) => {
      const aVal = a[columnIndex];
      const bVal = b[columnIndex];
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) return direction === 'asc' ? -1 : 1;
      if (bVal === null || bVal === undefined) return direction === 'asc' ? 1 : -1;
      
      // Try numeric comparison first
      if (isNumeric(aVal) && isNumeric(bVal)) {
        return direction === 'asc' 
          ? Number(aVal) - Number(bVal)
          : Number(bVal) - Number(aVal);
      }
      
      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (direction === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });
    
    return sorted;
  };

  // Check if a column is numeric
  const isNumericColumn = (rows, columnIndex) => {
    if (!Array.isArray(rows) || rows.length === 0) return false;
    
    let samplesChecked = 0;
    let validNumbers = 0;
    
    for (const row of rows) {
      if (!Array.isArray(row)) continue;
      
      const value = row[columnIndex];
      if (value === null || value === undefined || value === '') {
        continue;
      }
      
      samplesChecked += 1;
      
      if (isNumeric(value)) {
        validNumbers += 1;
      } else {
        return false;
      }
      
      if (samplesChecked >= 8) break;
    }
    
    return samplesChecked > 0 && validNumbers === samplesChecked;
  };

  // Filter rows by multiple filters (AND logic - all filters must match)
  const filterRowsMultiple = (rows, searchFilters) => {
    if (!searchFilters || searchFilters.length === 0) {
      return rows;
    }
    
    return rows.filter(row => {
      // Every filter must pass for the row to be included
      return searchFilters.every(filter => {
        const { column, term, operator } = filter;
        
        if (!term || term.trim() === '') {
          return true; // Empty filter passes everything
        }
        
        const cellValue = row[column];
        const searchTrimmed = term.trim();
        
        // Check if column is numeric
        const isNumeric = operator !== null && operator !== undefined;
        
        // Handle numerical comparison
        if (isNumeric && operator) {
          const searchNum = Number(searchTrimmed);
          if (isNaN(searchNum)) {
            return true; // Invalid number, pass this filter
          }
          
          if (cellValue === null || cellValue === undefined || cellValue === '') {
            return false;
          }
          
          const cellNum = Number(cellValue);
          if (isNaN(cellNum)) {
            return false;
          }
          
          switch (operator) {
            case '<':
              return cellNum < searchNum;
            case '>':
              return cellNum > searchNum;
            case '=':
              return cellNum === searchNum;
            default:
              return true;
          }
        }
        
        // Handle text-based search (contains)
        if (cellValue === null || cellValue === undefined) {
          return false;
        }
        
        // Normalize spaces: replace multiple spaces with single space, then trim
        const normalizedSearch = searchTrimmed.replace(/\s+/g, ' ').trim().toLowerCase();
        const cellStr = String(cellValue).replace(/\s+/g, ' ').trim().toLowerCase();
        return cellStr.includes(normalizedSearch);
      });
    });
  };

  // Group rows by column index
  const groupRows = (rows, columnIndex, options = {}) => {
    const { isDateColumn = false, dateGranularity = 'day' } = options;

    // Handle empty rows
    if (!rows || rows.length === 0) {
      return [];
    }

    if (columnIndex === null || columnIndex === undefined) {
      return rows.map(row => ({
        groupKey: null,
        rows: [row],
        aggregatedRow: row
      }));
    }
    
    const grouped = {};
    
    rows.forEach(row => {
      // Skip invalid rows
      if (!row || !Array.isArray(row)) {
        return;
      }
      
      const originalValue = row[columnIndex];
      let key;

      if (isDateColumn) {
        const formatted = formatDateByGranularity(originalValue, dateGranularity);
        if (formatted !== null) {
          key = formatted;
        } else {
          key = originalValue === null || originalValue === undefined ? '' : String(originalValue);
        }
      } else {
        key = originalValue === null || originalValue === undefined ? '' : String(originalValue);
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          groupKey: key,
          rows: []
        };
      }
      
      grouped[key].rows.push(row);
    });
    
    return Object.values(grouped).map(group => {
      // Ensure group has rows before processing
      if (!group.rows || group.rows.length === 0) {
        return null;
      }
      
      const aggregatedRow = group.rows[0].map((_, colIndex) => {
        if (colIndex === columnIndex) {
          return group.groupKey;
        }
        
        const values = group.rows
          .map(r => r[colIndex])
          .filter(val => val !== null && val !== undefined && val !== '');
        
        const shouldSum = values.length > 0 && values.every(val => isNumeric(val));
        
        if (shouldSum) {
          const total = values.reduce((sum, val) => sum + Number(val), 0);
          
          // Preserve decimal precision when original values were strings representing numbers
          const decimalPlaces = values.reduce((maxDecimals, currentValue) => {
            if (typeof currentValue === 'string' && currentValue.includes('.')) {
              const decimals = currentValue.split('.')[1].length;
              return Math.max(maxDecimals, decimals);
            }
            return maxDecimals;
          }, 0);
          
          if (decimalPlaces > 0) {
            return Number(total.toFixed(decimalPlaces));
          }
          
          return Number.isInteger(total) ? total : Number(total.toFixed(4));
        }
        
        return values.length > 0 ? values[0] : '';
      });
      
      return {
        ...group,
        aggregatedRow
      };
    }).filter(group => group !== null); // Filter out any null groups
  };

  // Handle sort toggle
  const handleSort = (messageId, columnIndex) => {
    setTableState(prev => {
      const current = prev[messageId] || { sortColumn: null, sortDirection: null, groupByColumn: null, groupByGranularity: null, groupByIsDateColumn: false };
      
      if (current.sortColumn === columnIndex) {
        // Toggle: asc -> desc -> null
        if (current.sortDirection === 'asc') {
          return {
            ...prev,
            [messageId]: { ...current, sortDirection: 'desc' }
          };
        } else if (current.sortDirection === 'desc') {
          return {
            ...prev,
            [messageId]: { ...current, sortColumn: null, sortDirection: null }
          };
        }
      }
      
      // New column or first click
      return {
        ...prev,
        [messageId]: { ...current, sortColumn: columnIndex, sortDirection: 'asc' }
      };
    });
  };

  // Handle group by change
  const handleGroupBy = (messageId, columnIndex, options = {}) => {
    const { isDateColumn = false, isMonthLevelDate = false } = options;

    setTableState(prev => {
      const current = prev[messageId] || { sortColumn: null, sortDirection: null, groupByColumn: null, groupByGranularity: null, groupByIsDateColumn: false, isMonthLevelDate: false };

      const isClearing = columnIndex === null || columnIndex === current.groupByColumn;
      const nextIsDateColumn = !isClearing && isDateColumn;
      const nextIsMonthLevelDate = !isClearing && isMonthLevelDate;

      // If it's a month-level date, default to 'month' granularity instead of 'day'
      const defaultGranularity = nextIsMonthLevelDate ? 'month' : 'day';

      return {
        ...prev,
        [messageId]: {
          ...current,
          groupByColumn: isClearing ? null : columnIndex,
          groupByGranularity: isClearing
            ? null
            : nextIsDateColumn
              ? (current.groupByGranularity || defaultGranularity)
              : null,
          groupByIsDateColumn: nextIsDateColumn,
          isMonthLevelDate: nextIsMonthLevelDate
        }
      };
    });
  };

  const handleDateGranularityChange = (messageId, granularity) => {
    if (!DATE_GRANULARITY_OPTIONS.some(option => option.value === granularity)) {
      return;
    }

    setTableState(prev => {
      const current = prev[messageId] || { sortColumn: null, sortDirection: null, groupByColumn: null, groupByGranularity: null, groupByIsDateColumn: false };

      if (!current.groupByIsDateColumn) {
        return prev;
      }

      return {
        ...prev,
        [messageId]: {
          ...current,
          groupByGranularity: granularity
        }
      };
    });
  };

  const handleNewChat = () => {
    // Clear all messages except the initial welcome message
    setMessages([{
      id: 1,
      type: 'agent',
      content: "Hello! I'm FlashAI. How can I help you with your database today?",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
    
    // Reset typing state
    setIsTyping(false);
    setCurrentStatus('');
    
    // Clear table state
    setTableState({});
    setOpenDropdowns({});
    setOpenSearchDropdowns({});
    setOpenOperatorDropdowns({});
    setOpenExportDropdowns({});
    setActiveFilterInput({});
    
    // Call the parent's onNewChat to create a fresh agent
    onNewChat();
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Start/stop a simple elapsed timer that only runs while the backend is executing.
    if (isExecuting) {
      // Only start/reset timer if it hasn't been started for this query yet
      if (!timerStartedRef.current) {
        // Reset timer for new query
        setExecutionTimer(0);
        executionTimerValueRef.current = 0;
        timerStartedRef.current = true;
      }
      
      // Start interval if not already running
      if (!executionTimerRef.current) {
        executionTimerRef.current = setInterval(() => {
          setExecutionTimer((prev) => {
            const newValue = prev + 1;
            executionTimerValueRef.current = newValue; // Keep ref in sync
            return newValue;
          });
        }, 1000);
      }
      return () => {
        // Don't clear interval here - let it continue running
      };
    }

    // Only stop timer when isExecuting becomes false (but don't reset value yet)
    if (executionTimerRef.current) {
      clearInterval(executionTimerRef.current);
      executionTimerRef.current = null;
    }
    // Don't reset timer value here - it will be captured when result arrives
  }, [isExecuting]);

  // Handle ESC key to close modals
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (showGuideModal) {
          setShowGuideModal(false);
        }
        if (showPromptsModal) {
          setShowPromptsModal(false);
        }
      }
    };

    if (showGuideModal || showPromptsModal) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when no modals are open
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      // Always restore overflow on cleanup
      document.body.style.overflow = '';
    };
  }, [showGuideModal, showPromptsModal]);

  // Load Excel data when prompts modal opens
  useEffect(() => {
    if (showPromptsModal) {
      const loadExcelData = async () => {
        try {
          // Add cache-busting query parameter and no-cache headers
          const timestamp = new Date().getTime();
          const response = await fetch(`/FlashAI_Prompts_Ver1.0.xlsx?t=${timestamp}`, {
            cache: 'no-store',
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          const arrayBuffer = await response.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          
          // Get the first sheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          setExcelData(jsonData);
        } catch (error) {
          console.error('Error loading Excel file:', error);
          setExcelData([]);
        }
      };
      loadExcelData();
    } else {
      // Clear Excel data when modal closes to ensure fresh load next time
      setExcelData(null);
    }
  }, [showPromptsModal]);

  useEffect(() => {
    // Initialize WebSocket connection
    // Construct WebSocket URL from current window location
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws/${dbConnection.connectionId}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected to:', wsUrl);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'thinking') {
        setIsTyping(true);
        setCurrentStatus('FlashAI is thinking...');
        setIsExecuting(false);
      } else if (data.type === 'status') {
        // Filter out reset_state status messages
        if (data.content && !data.content.includes('reset_state') && !data.content.includes('Preparing for next query')) {
          setIsTyping(true);
          // Translate the status message to user-friendly format
          const friendlyStatus = translateStatusMessage(data.content);
          setCurrentStatus(friendlyStatus);

          // Start timer ONLY when the user-facing status is "FlashAI is crafting a response"
          // (this text is produced by translateStatusMessage).
          setIsExecuting(friendlyStatus === 'FlashAI is crafting a response');
        } else if (data.content && data.content.includes('Preparing for next query')) {
          setIsTyping(false);
          setCurrentStatus('');
          setIsExecuting(false);
          // Add a friendly message when reset is complete (only if not already added)
          setMessages(prev => {
            const lastMessage = prev[prev.length - 1];
            if (!lastMessage || lastMessage.content !== "What else would you like to know?") {
              return [...prev, {
                id: Date.now(),
                type: 'agent',
                content: "What else would you like to know?",
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              }];
            }
            return prev;
          });
        }
      } else if (data.type === 'message' || data.type === 'no_results_message') {
        console.log('Received message:', data);
        setIsTyping(false);
        setCurrentStatus('');
        setIsExecuting(false);
        
        // Check if it's a no results message
        let messageContent = data.content;
        if (data.type === 'no_results_message') {
          messageContent = data.content || data.no_results_message;
        }
        
        console.log('Adding message to chat:', messageContent);
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'agent',
          content: messageContent,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isNoResults: data.type === 'no_results_message'
        }]);
      } else if (data.type === 'result') {
        // Capture the execution timer value from ref (more reliable than state)
        const finalExecutionTime = executionTimerValueRef.current || executionTimer;
        console.log('Capturing execution time:', finalExecutionTime, 'from ref:', executionTimerValueRef.current, 'from state:', executionTimer);
        // Stop the timer and reset for next query
        setIsExecuting(false);
        setIsTyping(false);
        setCurrentStatus('');
        timerStartedRef.current = false; // Reset flag for next query
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'agent',
          content: data.content,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          result: data.result,
          executionTime: finalExecutionTime
        }]);
      } else if (data.type === 'clarification') {
        setIsTyping(false);
        setCurrentStatus('');
        setIsExecuting(false);
        timerStartedRef.current = false; // Reset flag
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'agent',
          content: data.content,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          needsClarification: true
        }]);
      } else if (data.type === 'error') {
        setIsTyping(false);
        setCurrentStatus('');
        setIsExecuting(false);
        timerStartedRef.current = false; // Reset flag
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'agent',
          content: `Sorry, I encountered an error: ${data.content}. Let me start fresh for your next question.`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isError: true
        }]);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
    };

    return () => {
      ws.close();
    };
  }, [dbConnection.connectionId]);

  // Auto-resize textarea function
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const maxHeight = 200; // Maximum height in pixels (about 8-10 lines)
      textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
      textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    }
  }, []);

  // Adjust textarea height when input value changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  const submitQuery = async (queryText) => {
    if (!queryText?.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: queryText,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    // Reset timer flag for new query
    timerStartedRef.current = false;
    // Reset textarea height after clearing
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }, 0);

    try {
      // Send message via WebSocket
      console.log('Attempting to send message, WebSocket state:', wsRef.current?.readyState);
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        const messageToSend = {
          type: 'message',
          content: queryText
        };
        console.log('Sending message:', messageToSend);
        wsRef.current.send(JSON.stringify(messageToSend));
        console.log('Message sent successfully');
      } else {
        console.error('WebSocket is not open. State:', wsRef.current?.readyState);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'agent',
        content: 'Sorry, there was an error processing your request.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitQuery(inputValue);
  };

  const handleRunSavedQuery = (queryText) => {
    if (!queryText?.trim()) return;
    setInputValue(queryText);
    setSavedOpen(false);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(queryText.length, queryText.length);
      }
    }, 0);
  };

  const renderMessage = (message) => {
    const isUser = message.type === 'user';
    
    return (
      <div key={message.id} className={`flex items-end gap-3 ${isUser ? 'justify-end' : ''}`}>
        <div className={`flex flex-1 flex-col gap-1 ${isUser ? 'items-end' : 'items-start'} min-w-0`}>
          {!isUser && (
            <p className="text-sm font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              FlashAI
            </p>
          )}
          {isUser && (
            <p className="text-sm font-semibold text-green-600 dark:text-green-400">
              {getUserDisplayName(session)}
            </p>
          )}
          
          <div className={`flex ${message.result ? 'max-w-full' : 'max-w-xs sm:max-w-md'} rounded-lg px-3 sm:px-4 py-2 sm:py-3 min-w-0 ${
            isUser 
              ? 'bg-user-bubble dark:bg-primary/30 rounded-br-none' 
              : 'bg-agent-bubble dark:bg-gray-700 rounded-bl-none'
          } text-text-main dark:text-white`}>
            {message.result ? (
              <div className="flex flex-col max-w-full">
                {message.content && (
                  <p className="text-base font-normal leading-normal pb-2">{message.content}</p>
                )}
                
                {/* Get current table state for this message */}
                {(() => {
                  const state = tableState[message.id] || { sortColumn: null, sortDirection: null, groupByColumn: null, groupByGranularity: null, groupByIsDateColumn: false, isMonthLevelDate: false, searchFilters: [] };
                  const groupByOpen = openDropdowns[message.id] || false;
                  const searchDropdownOpen = openSearchDropdowns[message.id] || false;
                  const operatorDropdownsOpen = openOperatorDropdowns[message.id] || {};
                  const exportDropdownOpen = openExportDropdowns[message.id] || false;
                  const searchFilters = state.searchFilters || [];
                  const groupByColumnIndex = state.groupByColumn;
                  const isDateGroupingColumn = state.groupByIsDateColumn;
                  // Re-check if it's a month-level date when rendering (in case state wasn't set correctly)
                  const detectedIsMonthLevel = groupByColumnIndex !== null 
                    ? isMonthLevelDate(message.result.rows, groupByColumnIndex)
                    : false;
                  const isMonthLevelDateColumn = state.isMonthLevelDate || detectedIsMonthLevel;
                  const effectiveGranularity = isDateGroupingColumn ? (state.groupByGranularity || (isMonthLevelDateColumn ? 'month' : 'day')) : null;
                  const currentGranularity = effectiveGranularity || 'day';
                  const currentGranularityIndex = DATE_GRANULARITY_OPTIONS.findIndex(option => option.value === currentGranularity);
                  
                  // Process data: filter first, then group (aggregate to one row per group), then sort
                  let processedRows = message.result.rows;
                  let isGrouped = false;
                  let groupedData = null;
                  
                  // Apply multiple search filters first (before grouping)
                  if (searchFilters.length > 0) {
                    processedRows = filterRowsMultiple(processedRows, searchFilters);
                  }
                  
                  // Capture filtered row count BEFORE grouping (important for display)
                  const filteredRowCount = processedRows.length;
                  
                  if (groupByColumnIndex !== null) {
                    // Group and aggregate - create a single aggregated row per group
                    // Only group if we have rows to group
                    if (processedRows.length > 0) {
                      groupedData = groupRows(processedRows, groupByColumnIndex, {
                        isDateColumn: isDateGroupingColumn,
                        dateGranularity: currentGranularity
                      });
                      processedRows = groupedData.map(group => group.aggregatedRow);
                      isGrouped = true;
                    } else {
                      // No rows to group, but still mark as grouped for UI consistency
                      isGrouped = true;
                      groupedData = [];
                      processedRows = [];
                    }
                  }
                  
                  const originalRowCount = message.result.rows.length;
                  const currentGroupedRowCount = groupedData ? groupedData.length : filteredRowCount;
                  const shouldDisableHigherGranularity =
                    isDateGroupingColumn &&
                    groupedData &&
                    currentGroupedRowCount <= 1 &&
                    currentGranularityIndex !== -1;
                  
                  const disabledGranularityMap = {};
                  if (isDateGroupingColumn && currentGranularityIndex !== -1) {
                    DATE_GRANULARITY_OPTIONS.forEach((option, idx) => {
                      // Disable 'day' option if the date is already at month level
                      if (isMonthLevelDateColumn && option.value === 'day') {
                        disabledGranularityMap[option.value] = true;
                      } else if (shouldDisableHigherGranularity && idx > currentGranularityIndex) {
                        disabledGranularityMap[option.value] = true;
                      } else {
                        disabledGranularityMap[option.value] = false;
                      }
                    });
                  }
                  
                  // Apply sorting (works for both grouped and non-grouped data)
                  if (state.sortColumn !== null && state.sortDirection) {
                    processedRows = sortRows(processedRows, state.sortColumn, state.sortDirection);
                  }
                  
                  // For display, limit to 10 rows
                  const displayData = processedRows.slice(0, 12);
                  const totalRows = isGrouped ? processedRows.length : filteredRowCount;
                  
                  return (
                    <>
                      {/* Export Buttons, Search Filters, and Group By */}
                      <div className="mb-2 flex flex-wrap gap-2 items-center">
                        {/* Add Filter Button */}
                        <div className="relative">
                          <button
                            onClick={() => {
                              if (searchFilters.length >= 5) {
                                alert('Maximum 5 filters allowed');
                                return;
                              }
                              setOpenSearchDropdowns(prev => ({ ...prev, [message.id]: !searchDropdownOpen }));
                            }}
                            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium rounded-md transition-colors ${
                              searchFilters.length >= 5
                                ? 'text-gray-400 bg-gray-200 cursor-not-allowed dark:bg-gray-600 dark:text-gray-500'
                                : 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                            }`}
                            disabled={searchFilters.length >= 5}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            <span className="hidden sm:inline">Add Filter</span>
                            <span className="sm:hidden">Filter</span>
                            <svg className={`w-3 h-3 transition-transform ${searchDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          
                          {searchDropdownOpen && searchFilters.length < 5 && (
                            <>
                              <div 
                                className="fixed inset-0 z-10" 
                                onClick={() => setOpenSearchDropdowns(prev => ({ ...prev, [message.id]: false }))}
                              ></div>
                              <div className="absolute z-20 mt-1 w-48 sm:w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto">
                                <div className="py-1">
                                  {message.result.columns.map((col, idx) => {
                                    // Check if this column already has a filter
                                    const alreadyFiltered = searchFilters.some(f => f.column === idx);
                                    if (alreadyFiltered) return null;
                                    
                                    return (
                                      <button
                                        key={idx}
                                        onClick={() => {
                                          const columnIsNumeric = isNumericColumn(message.result.rows, idx);
                                          const newFilter = {
                                            id: `${Date.now()}-${Math.random()}`,
                                            column: idx,
                                            term: '',
                                            operator: columnIsNumeric ? '=' : null
                                          };
                                          setTableState(prev => ({
                                            ...prev,
                                            [message.id]: {
                                              ...(prev[message.id] || {}),
                                              searchFilters: [...(prev[message.id]?.searchFilters || []), newFilter]
                                            }
                                          }));
                                          setActiveFilterInput(prev => ({ ...prev, [message.id]: newFilter.id }));
                                          setOpenSearchDropdowns(prev => ({ ...prev, [message.id]: false }));
                                        }}
                                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                      >
                                        {col}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Filter Chips */}
                        {searchFilters.map((filter) => {
                          const columnName = message.result.columns[filter.column];
                          const isNumeric = filter.operator !== null;
                          const isActive = activeFilterInput[message.id] === filter.id;
                          const operatorDropdownOpen = operatorDropdownsOpen[filter.id] || false;
                          
                          return (
                            <div key={filter.id} className="flex items-center gap-2">
                              {/* Filter Chip Container */}
                              <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-sm font-medium rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-md hover:shadow-lg transition-shadow">
                                {/* Column Name */}
                                <span className="hidden sm:inline whitespace-nowrap">{columnName}:</span>
                                <span className="sm:hidden whitespace-nowrap">{columnName.length > 8 ? columnName.substring(0, 8) + '...' : columnName}:</span>
                                
                                {/* Operator Bubble for numeric columns */}
                                {isNumeric && (
                                  <div className="relative">
                                    <button
                                      onClick={() => setOpenOperatorDropdowns(prev => ({ 
                                        ...prev, 
                                        [message.id]: { ...(prev[message.id] || {}), [filter.id]: !operatorDropdownOpen }
                                      }))}
                                      className="px-2.5 py-1 rounded-lg bg-green-500 hover:bg-green-400 font-bold text-sm shadow-sm hover:shadow transition-all duration-200 flex items-center justify-center min-w-[2rem]"
                                      title="Change operator"
                                    >
                                      {filter.operator || '='}
                                      <svg className="ml-1 w-3 h-3 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </button>
                                    
                                    {operatorDropdownOpen && (
                                      <>
                                        <div 
                                          className="fixed inset-0 z-10" 
                                          onClick={() => setOpenOperatorDropdowns(prev => ({ 
                                            ...prev, 
                                            [message.id]: { ...(prev[message.id] || {}), [filter.id]: false }
                                          }))}
                                        ></div>
                                        <div className="absolute z-20 mt-2 w-36 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                          <div className="py-1">
                                            {['<', '>', '='].map(op => (
                                              <button
                                                key={op}
                                                onClick={() => {
                                                  setTableState(prev => ({
                                                    ...prev,
                                                    [message.id]: {
                                                      ...(prev[message.id] || {}),
                                                      searchFilters: (prev[message.id]?.searchFilters || []).map(f => 
                                                        f.id === filter.id ? { ...f, operator: op } : f
                                                      )
                                                    }
                                                  }));
                                                  setOpenOperatorDropdowns(prev => ({ 
                                                    ...prev, 
                                                    [message.id]: { ...(prev[message.id] || {}), [filter.id]: false }
                                                  }));
                                                }}
                                                className={`w-full text-left px-4 py-2 text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                                  filter.operator === op
                                                    ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                    : 'text-gray-700 dark:text-gray-300'
                                                }`}
                                              >
                                                {op === '<' ? 'Less than (<)' : op === '>' ? 'Greater than (>)' : 'Equal to (=)'}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                                
                                {/* Input Field with rounded edges */}
                                <input
                                  type={isNumeric ? "number" : "text"}
                                  value={filter.term || ''}
                                  onChange={(e) => {
                                    setTableState(prev => ({
                                      ...prev,
                                      [message.id]: {
                                        ...(prev[message.id] || {}),
                                        searchFilters: (prev[message.id]?.searchFilters || []).map(f => 
                                          f.id === filter.id ? { ...f, term: e.target.value } : f
                                        )
                                      }
                                    }));
                                  }}
                                  onClick={() => setActiveFilterInput(prev => ({ ...prev, [message.id]: filter.id }))}
                                  onBlur={() => {
                                    setTimeout(() => {
                                      if (activeFilterInput[message.id] === filter.id) {
                                        setActiveFilterInput(prev => ({ ...prev, [message.id]: null }));
                                      }
                                    }, 200);
                                  }}
                                  placeholder={isNumeric ? "value" : "text"}
                                  className={`w-20 sm:w-28 px-3 py-1.5 text-sm bg-white/95 dark:bg-gray-800/95 text-gray-900 dark:text-white rounded-lg border-2 transition-all duration-200 ${
                                    isActive 
                                      ? 'border-yellow-400 shadow-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-50' 
                                      : 'border-white/30 dark:border-gray-600/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white'
                                  }`}
                                  autoFocus={isActive}
                                />
                                
                                {/* Remove Button */}
                                <button
                                  onClick={() => {
                                    setTableState(prev => ({
                                      ...prev,
                                      [message.id]: {
                                        ...(prev[message.id] || {}),
                                        searchFilters: (prev[message.id]?.searchFilters || []).filter(f => f.id !== filter.id)
                                      }
                                    }));
                                    setActiveFilterInput(prev => ({ ...prev, [message.id]: null }));
                                  }}
                                  className="ml-1 hover:bg-green-700/80 rounded-lg p-1.5 transition-all duration-200 hover:scale-110"
                                  title="Remove filter"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Clear All Filters Button */}
                        {searchFilters.length > 0 && (
                          <button
                            onClick={() => {
                              setTableState(prev => ({
                                ...prev,
                                [message.id]: {
                                  ...(prev[message.id] || {}),
                                  searchFilters: []
                                }
                              }));
                              setActiveFilterInput(prev => ({ ...prev, [message.id]: null }));
                            }}
                            className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:text-red-400 rounded-md transition-colors"
                            title="Clear all filters"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span className="hidden sm:inline">Clear All</span>
                          </button>
                        )}
                        
                        {/* Export Dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setOpenExportDropdowns(prev => ({ ...prev, [message.id]: !exportDropdownOpen }))}
                            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
                            title="Export data"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="hidden sm:inline">Export</span>
                            <svg className={`w-3 h-3 transition-transform ${exportDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          
                          {exportDropdownOpen && (
                            <>
                              <div 
                                className="fixed inset-0 z-10" 
                                onClick={() => setOpenExportDropdowns(prev => ({ ...prev, [message.id]: false }))}
                              ></div>
                              <div className="absolute z-20 mt-1 w-48 sm:w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700">
                                <div className="py-1">
                                  {/* Export Filtered Excel - Show if filters or grouping are active */}
                                  {(() => {
                                    const hasFilter = searchFilters.length > 0 && searchFilters.some(f => f.term && f.term.trim() !== '');
                                    const hasGrouping = state.groupByColumn !== null;
                                    const processedRows = getProcessedRowsForExport(message.result, message.id);
                                    const filteredRowCount = processedRows.length;
                                    const isFiltered = hasFilter || hasGrouping;
                                    
                                    return isFiltered && filteredRowCount > 0 && filteredRowCount <= 50000 ? (
                                      <>
                                        <button
                                          onClick={() => {
                                            exportToExcel(message.result, message.id, true, true);
                                            setOpenExportDropdowns(prev => ({ ...prev, [message.id]: false }));
                                          }}
                                          className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-blue-700 dark:text-blue-400 font-medium"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          </svg>
                                          <span>Export Filtered Excel ({filteredRowCount} rows)</span>
                                        </button>
                                        <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                      </>
                                    ) : null;
                                  })()}
                                  
                                  {/* Export to Excel - Only show if rows <= 50000 */}
                                  {message.result.rows.length <= 50000 && (
                                    <>
                                      {/* Export to Excel - Preview */}
                                      <button
                                        onClick={() => {
                                          exportToExcel(message.result, message.id, false);
                                          setOpenExportDropdowns(prev => ({ ...prev, [message.id]: false }));
                                        }}
                                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-green-700 dark:text-green-400"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span>Export to Excel (Preview)</span>
                                      </button>
                                      
                                      {/* Export to Excel - All */}
                                      {message.result.rows.length > 12 && (
                                        <button
                                          onClick={() => {
                                            exportToExcel(message.result, message.id, true);
                                            setOpenExportDropdowns(prev => ({ ...prev, [message.id]: false }));
                                          }}
                                          className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-green-700 dark:text-green-400"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          </svg>
                                          <span>Export to Excel (All {message.result.rows.length} rows)</span>
                                        </button>
                                      )}
                                      
                                      {/* Divider - Only show if Excel options are visible */}
                                      <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                    </>
                                  )}
                                  
                                  {/* Export to CSV */}
                                  <button
                                    onClick={() => {
                                      exportToPowerBI(message.result, message.id);
                                      setOpenExportDropdowns(prev => ({ ...prev, [message.id]: false }));
                                    }}
                                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-purple-700 dark:text-purple-400"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <span>Export to CSV</span>
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Group By Dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setOpenDropdowns(prev => ({ ...prev, [message.id]: !groupByOpen }))}
                            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium rounded-md transition-colors ${
                              state.groupByColumn !== null
                                ? 'text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                                : 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                            }`}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                            </svg>
                            <span className="hidden sm:inline">
                              {state.groupByColumn !== null 
                                ? `Grouped by: ${message.result.columns[state.groupByColumn]}`
                                : 'Group By'}
                            </span>
                            <span className="sm:hidden">Group</span>
                            <svg className={`w-3 h-3 transition-transform ${groupByOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          
                          {groupByOpen && (
                            <>
                              <div 
                                className="fixed inset-0 z-10" 
                                onClick={() => setOpenDropdowns(prev => ({ ...prev, [message.id]: false }))}
                              ></div>
                              <div className="absolute z-20 mt-1 w-48 sm:w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto">
                                <div className="py-1">
                                  {state.groupByColumn !== null && (
                                    <button
                                      onClick={() => {
                                        handleGroupBy(message.id, null);
                                        setOpenDropdowns(prev => ({ ...prev, [message.id]: false }));
                                      }}
                                      className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:text-red-400"
                                    >
                                      Clear Grouping
                                    </button>
                                  )}
                                  {message.result.columns.map((col, idx) => {
                                    const columnIsNumeric = message.result.rows.every(row => {
                                      const value = row[idx];
                                      if (value === null || value === undefined || value === '') return true;
                                      return isNumeric(value);
                                    });

                                    if (columnIsNumeric) {
                                      return null;
                                    }

                                    return (
                                      <button
                                        key={idx}
                                        onClick={() => {
                                          // Check if it's month-level first (MM-YYYY format)
                                          const columnIsMonthLevel = isMonthLevelDate(message.result.rows, idx);
                                          // If it's month-level, it's also a date column
                                          const columnIsDate = columnIsMonthLevel || isDateColumnLike(message.result.rows, idx);
                                          handleGroupBy(message.id, idx, { isDateColumn: columnIsDate, isMonthLevelDate: columnIsMonthLevel });
                                          setOpenDropdowns(prev => ({ ...prev, [message.id]: false }));
                                        }}
                                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                          state.groupByColumn === idx
                                            ? 'bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 text-blue-600 dark:text-blue-400'
                                            : 'text-gray-700 dark:text-gray-300'
                                        }`}
                                      >
                                        {col}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {isDateGroupingColumn && state.groupByColumn !== null && (
                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                            <span>Group dates by:</span>
                            {(() => {
                              const cycleGranularity = () => {
                                if (!isDateGroupingColumn) return currentGranularity;
                                
                                const options = DATE_GRANULARITY_OPTIONS;
                                const currentIndex = options.findIndex(option => option.value === currentGranularity);
                                if (currentIndex === -1) return currentGranularity;
                                
                                for (let i = 1; i <= options.length; i++) {
                                  const nextIndex = (currentIndex + i) % options.length;
                                  const nextOption = options[nextIndex];
                                  if (!disabledGranularityMap[nextOption.value]) {
                                    return nextOption.value;
                                  }
                                }
                                
                                return currentGranularity;
                              };
                              
                              const nextGranularity = cycleGranularity();
                              const currentOption = DATE_GRANULARITY_OPTIONS.find(option => option.value === currentGranularity);
                              
                              return (
                                <button
                                  onClick={() => {
                                    if (!isDateGroupingColumn) return;
                                    const targetGranularity = cycleGranularity();
                                    if (targetGranularity !== currentGranularity) {
                                      handleDateGranularityChange(message.id, targetGranularity);
                                    }
                                  }}
                                  disabled={nextGranularity === currentGranularity}
                                  className={`px-2 py-1 rounded-md border transition-colors ${
                                    nextGranularity === currentGranularity
                                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white border-transparent cursor-default'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                                  }`}
                                  title={
                                    nextGranularity === currentGranularity
                                      ? 'No additional aggregation levels available'
                                      : 'Click to change date grouping granularity'
                                  }
                                >
                                  {currentOption ? currentOption.label : 'Day'}
                                </button>
                              );
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Row Count Info */}
                      <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                        {searchFilters.length > 0 && searchFilters.some(f => f.term && f.term.trim() !== '') && (
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            Filtered: {filteredRowCount} of {originalRowCount} rows ({searchFilters.filter(f => f.term && f.term.trim() !== '').length} {searchFilters.filter(f => f.term && f.term.trim() !== '').length === 1 ? 'filter' : 'filters'}) • 
                          </span>
                        )}
                        {isGrouped ? (
                          <>
                            Showing {displayData.length} of {totalRows} grouped rows
                            {totalRows > 12 && (
                              <span className="ml-2 text-blue-600 dark:text-blue-400">
                                • Download to see all rows
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            Showing {displayData.length} of {totalRows} rows
                            {totalRows > 12 && (
                              <span className="ml-2 text-blue-600 dark:text-blue-400">
                                • Download to see all rows
                              </span>
                            )}
                          </>
                        )}
                      </div>

                      {/* Table Container with Horizontal Scroll */}
                      <div className="overflow-x-auto border border-gray-200 dark:border-gray-600 rounded-lg max-w-full">
                        <table className="min-w-full text-sm text-left text-gray-500 dark:text-gray-400">
                          <thead className="text-xs text-gray-700 uppercase bg-gray-200 dark:bg-gray-600 dark:text-gray-300">
                            <tr>
                              {message.result.columns.map((col, index) => {
                                const isSorted = state.sortColumn === index;
                                const sortDir = isSorted ? state.sortDirection : null;
                                return (
                                  <th 
                                    key={index} 
                                    className={`px-4 py-2 whitespace-nowrap ${index === 0 ? 'rounded-tl-md' : ''} ${index === message.result.columns.length - 1 ? 'rounded-tr-md' : ''}`}
                                    scope="col"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <span>{col}</span>
                                      <button
                                        onClick={() => handleSort(message.id, index)}
                                        className={`flex-shrink-0 p-0.5 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors ${
                                          isSorted ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'
                                        }`}
                                        title={sortDir === 'asc' ? 'Sort Descending' : sortDir === 'desc' ? 'Clear Sort' : 'Sort Ascending'}
                                      >
                                        {sortDir === 'asc' ? (
                                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                          </svg>
                                        ) : sortDir === 'desc' ? (
                                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                          </svg>
                                        ) : (
                                          <svg className="w-3.5 h-3.5 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                          </svg>
                                        )}
                                      </button>
                                    </div>
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {/* Render data (works for both grouped and non-grouped) */}
                            {displayData.map((row, rowIndex) => {
                              const isLastRow = rowIndex === displayData.length - 1;
                              return (
                                <tr key={rowIndex} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                                  {row.map((cell, cellIndex) => (
                                    <td 
                                      key={cellIndex} 
                                      className={`px-4 py-2 font-medium text-gray-900 whitespace-nowrap dark:text-white ${
                                        isLastRow && cellIndex === 0 ? 'rounded-bl-md' : ''
                                      } ${
                                        isLastRow && cellIndex === row.length - 1 ? 'rounded-br-md' : ''
                                      }`}
                                    >
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      
                    </>
                  );
                })()}
                
                {/* Execution Time Display - Outside table, at bottom of result bubble */}
                {message.executionTime !== null && message.executionTime !== undefined && message.executionTime >= 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-right">
                      database execution time : {formatElapsed(message.executionTime)}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <p className="text-base font-normal leading-normal">{message.content}</p>
                {isUser && (
                  <button
                    type="button"
                    onClick={() => handleToggleSaveQuery(message.content)}
                    className="flex-shrink-0 p-1 rounded-full hover:bg-white/20 transition-colors"
                    title={savedQuerySet.has(message.content) ? 'Unsave query' : 'Save query'}
                    aria-label={savedQuerySet.has(message.content) ? 'Unsave query' : 'Save query'}
                  >
                    {savedQuerySet.has(message.content) ? (
                      <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.449a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.449a1 1 0 00-1.175 0l-3.37 2.449c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.05 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.287-3.957z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-gray-300 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.518 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.977 2.89a1 1 0 00-.364 1.118l1.518 4.674c.3.921-.755 1.688-1.54 1.118l-3.977-2.89a1 1 0 00-1.175 0l-3.977 2.89c-.784.57-1.838-.197-1.539-1.118l1.518-4.674a1 1 0 00-.364-1.118L2.05 10.1c-.783-.57-.38-1.81.588-1.81h4.915a1 1 0 00.95-.69l1.518-4.674z" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
          
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {message.timestamp}
          </p>
        </div>
        
        {isUser && (
          <div className="bg-center bg-no-repeat aspect-square bg-cover rounded-full w-8 h-8 sm:w-10 sm:h-10 shrink-0" style={{backgroundImage: 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuDKKgXqs2qufXFDH6KGUGpbt0KPurCRN_vF6SSdCXiZThm8snX2D85PJOgXMUHqTkRPmD1ijc7Ci1KJsY2eywAuiSB2Gyf0IpbkH-vyrwGYMU5Pm7_Vrorh8pp_vXrtlPtWac8cYiK6Yu5W1V9Xb1JksorevpfwoiEeo39MgEvNNncgZOJinFziLgDN-KNW65-QMchs57jVbw-A7K5N3NQ59sj-_2cyIJdzAeCkif7m7ZnyCctFBzQfSl4n0Eb-00VC4a2LJLAp6Zo")'}}></div>
        )}
      </div>
    );
  };

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col group/design-root overflow-x-hidden">
      <div className="layout-container flex h-full grow flex-col">
        <div className="flex flex-1 justify-center">
          <div className="layout-content-container flex flex-col w-full max-w-4xl flex-1 min-w-0">
            <header className="flex items-center justify-between border-b border-solid border-gray-200 dark:border-gray-700 px-3 sm:px-6 py-3 sm:py-4">
              <div className="flex flex-col items-start gap-0.5">
                <div className="flex items-center gap-2 sm:gap-3 text-text-main dark:text-white">
                  <h2 className="text-xl sm:text-2xl font-bold leading-tight tracking-tight bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">FlashAI</h2>
                </div>
                <p className="text-xs italic text-slate-500 dark:text-slate-400 ml-0.5">product of Fiorifox Technologies</p>
              </div>
              <div className="flex items-center gap-1 sm:gap-3">
                <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
                <span className="hidden sm:inline text-sm font-medium text-gray-600 dark:text-gray-300">
                  Connected to: {dbConnection.database}
                </span>
                <span className="sm:hidden text-xs font-medium text-gray-600 dark:text-gray-300">
                  {dbConnection.database}
                </span>
                <button 
                  onClick={handleNewChat}
                  className="flex items-center gap-1 sm:gap-2 rounded-lg btn-gradient px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-white transition-all duration-300 hover:shadow-lg"
                >
                  <svg fill="currentColor" height="14" viewBox="0 0 256 256" width="14" xmlns="http://www.w3.org/2000/svg">
                    <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
                  </svg>
                  <span className="hidden sm:inline">New Chat</span>
                  <span className="sm:hidden">New</span>
                </button>
                <button 
                  onClick={() => setShowGuideModal(true)}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg transition-colors shadow-md"
                  title="FlashAI Features"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span className="hidden sm:inline">FlashAI Features</span>
                </button>
                <button 
                  onClick={() => setShowPromptsModal(true)}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 rounded-lg transition-colors shadow-md"
                  title="FlashAI Prompts"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="hidden sm:inline">FlashAI Prompts</span>
                </button>
                <button 
                  onClick={onBack}
                  className="text-xs sm:text-sm text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  <span className="hidden sm:inline">← Back</span>
                  <span className="sm:hidden">←</span>
                </button>
              </div>
            </header>
            
            <main className="flex-1 flex flex-col p-3 sm:p-6 space-y-4 sm:space-y-6">
              <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto">
                {messages.map(renderMessage)}
                
                {isTyping && (
                  <div className="flex items-end gap-3">
                    <div className="flex flex-1 flex-col gap-1 items-start">
                      <p className="text-sm font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        FlashAI
                      </p>
                      <div className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-agent-bubble dark:bg-gray-700 rounded-lg rounded-bl-none">
                        <div className="flex space-x-1">
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full animate-bounce"></div>
                        </div>
                        <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                          <span>{currentStatus}</span>
                          {isExecuting && (
                            <span className="px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600">
                              {formatElapsed(executionTimer)}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            </main>
            
            <div className="sticky bottom-0 bg-background-light dark:bg-background-dark py-3 sm:py-4 px-3 sm:px-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-end gap-2 sm:gap-3">
                <div className="flex flex-col min-w-0 flex-1">
                  <div
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      savedOpen ? 'max-h-64 opacity-100 mb-2' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-2">
                      {savedLoading ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1">Loading saved queries...</p>
                      ) : savedQueries.length === 0 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1">No saved queries yet.</p>
                      ) : (
                        <div className="flex flex-col gap-1 max-h-52 overflow-y-auto">
                          {savedQueries.map((item) => (
                            <div
                              key={item.id}
                              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleSaveQuery(item.query);
                                }}
                                className="flex-shrink-0 p-1 rounded-full hover:bg-white/30 transition-colors"
                                title="Unsave query"
                                aria-label="Unsave query"
                              >
                                <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.449a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.449a1 1 0 00-1.175 0l-3.37 2.449c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.05 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.287-3.957z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRunSavedQuery(item.query)}
                                className="text-left text-sm text-gray-800 dark:text-gray-100 flex-1"
                                title="Copy to input"
                              >
                                {item.query}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                <form onSubmit={handleSubmit} className="flex flex-col min-w-0 flex-1">
                  <div className="flex w-full items-end gap-2 sm:gap-3">
                    <textarea
                      ref={textareaRef}
                      className="form-input flex w-full min-w-0 flex-1 resize-none rounded-lg text-text-main dark:text-white focus:outline-0 focus:ring-2 focus:ring-primary border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:border-primary placeholder:text-gray-400 dark:placeholder-gray-500 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base font-normal leading-normal transition-all duration-300 shadow-sm hover:shadow-md min-h-[2.5rem] sm:min-h-[3rem] max-h-[200px] overflow-y-auto"
                      placeholder="Ask me anything..." 
                      value={inputValue}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                        adjustTextareaHeight();
                      }}
                      onKeyDown={(e) => {
                        // Submit on Enter, but allow Shift+Enter for new line
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmit(e);
                        }
                      }}
                      disabled={isLoading}
                      rows={1}
                    />
                    <button
                      type="button"
                      onClick={() => setSavedOpen((prev) => !prev)}
                      className="flex items-center gap-1 rounded-lg h-[2.5rem] sm:h-[3rem] px-2 sm:px-3 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 flex-shrink-0"
                      title="Saved queries"
                    >
                      <svg className={`w-4 h-4 transition-transform ${savedOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                      <span className="hidden sm:inline">Saved</span>
                    </button>
                    <button 
                      className="flex items-center justify-center rounded-lg h-[2.5rem] sm:h-[3rem] px-3 sm:px-4 btn-gradient text-white text-xs sm:text-sm font-medium hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-background-dark disabled:opacity-50 transition-all duration-300 flex-shrink-0" 
                      type="submit"
                      disabled={isLoading || !inputValue.trim()}
                    >
                      <span className="hidden sm:inline">Send</span>
                      <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </button>
                  </div>
                </form>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Guide PDF Modal */}
      {showGuideModal && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70 p-4"
          onClick={() => setShowGuideModal(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowGuideModal(false);
            }
          }}
          tabIndex={-1}
        >
          <div 
            className="relative w-full max-w-[95vw] h-[95vh] bg-white dark:bg-gray-800 rounded-lg shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Floating Close Button - Always visible */}
            <button
              onClick={() => setShowGuideModal(false)}
              className="absolute top-2 right-2 z-[100] p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
              aria-label="Close"
              title="Close (ESC)"
            >
              <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {/* Modal Header - Sticky at top */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">FlashAI User Guide</h2>
              <div className="flex items-center gap-2">
                {/* Download Button */}
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = '/FlashAI_Features_Ver1.0.pdf';
                    link.download = 'FlashAI_Features_Ver1.0.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download PDF</span>
                </button>
                {/* Close Button */}
                <button
                  onClick={() => setShowGuideModal(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="Close"
                  title="Close (ESC)"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* PDF Viewer */}
            <div className="flex-1 overflow-hidden min-h-0">
              <iframe
                src="/FlashAI_Features_Ver1.0.pdf#toolbar=1&navpanes=1&scrollbar=1"
                className="w-full h-full border-0"
                title="FlashAI User Guide"
                style={{ minHeight: 0 }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Prompts Excel Modal */}
      {showPromptsModal && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70 p-4"
          onClick={() => setShowPromptsModal(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowPromptsModal(false);
            }
          }}
          tabIndex={-1}
        >
          <div 
            className="relative w-full max-w-[95vw] h-[95vh] bg-white dark:bg-gray-800 rounded-lg shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Floating Close Button - Always visible */}
            <button
              onClick={() => setShowPromptsModal(false)}
              className="absolute top-2 right-2 z-[100] p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
              aria-label="Close"
              title="Close (ESC)"
            >
              <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {/* Modal Header - Sticky at top */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">FlashAI Prompts</h2>
              <div className="flex items-center gap-2">
                {/* Download Button */}
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = '/FlashAI_Prompts_Ver1.0.xlsx';
                    link.download = 'FlashAI_Prompts_Ver1.0.xlsx';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download Excel</span>
                </button>
                {/* Close Button */}
                <button
                  onClick={() => setShowPromptsModal(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="Close"
                  title="Close (ESC)"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Excel Viewer */}
            <div className="flex-1 overflow-auto min-h-0 p-4">
              {excelData ? (
                excelData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800">
                      <thead>
                        {excelData[0] && (
                          <tr className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
                            {excelData[0].map((header, index) => (
                              <th
                                key={index}
                                className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left font-semibold"
                              >
                                {header || `Column ${index + 1}`}
                              </th>
                            ))}
                          </tr>
                        )}
                      </thead>
                      <tbody>
                        {excelData.slice(1).map((row, rowIndex) => (
                          <tr
                            key={rowIndex}
                            className={`${
                              rowIndex % 2 === 0
                                ? 'bg-gray-50 dark:bg-gray-900'
                                : 'bg-white dark:bg-gray-800'
                            } hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
                          >
                            {row.map((cell, cellIndex) => (
                              <td
                                key={cellIndex}
                                className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-900 dark:text-gray-100"
                              >
                                {cell || ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                    <p>No data available</p>
                  </div>
                )
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading Excel data...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
